import { useState, useEffect, useCallback, useMemo } from "react";
import { GetMyBookingsAction } from "../../../../store/action";
import { fromWallClock } from "../../../../utils/dates";

/**
 * Loads the signed-in customer's bookings and derives the few things both
 * customer pages need from them.
 *
 * Shared by Overview and MyBookings so the two never disagree about what
 * "upcoming" means — they render the same numbers on the same screen, and a
 * split-brain definition would show a "2 upcoming" tile above a list of three.
 */
export const useMyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetMyBookingsAction();
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || "loadFailed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const derived = useMemo(() => {
    const now = Date.now();

    // Wall clock, not an instant — the stored value carries a `Z` it does not
    // mean, so `new Date` would sort and bucket every booking by the viewer's
    // offset instead of the time the customer actually booked.
    const endOf = (b) =>
      fromWallClock(b.bookingDateTime).getTime() + (b.duration || 0) * 60000;

    // Cancelled and Completed are terminal: the business has closed the booking,
    // so it belongs in history whatever its clock time says. Without this a
    // booking marked Completed early still sat under "Upcoming" while the card
    // itself refused to change it — two rules, one booking, opposite answers.
    const isFinished = (b) =>
      ["cancelled", "completed"].includes(b.status?.toLowerCase());

    // Otherwise a booking is "upcoming" until it has finished, not until it has
    // started: an appointment you are currently sitting in is still the one you
    // care about, and dropping it into history the moment it begins is how it
    // vanishes from the top of the page mid-visit.
    const upcoming = bookings
      .filter((b) => !isFinished(b) && endOf(b) >= now)
      .sort(
        (a, b) =>
          fromWallClock(a.bookingDateTime) - fromWallClock(b.bookingDateTime),
      );

    const past = bookings
      .filter((b) => isFinished(b) || endOf(b) < now)
      .sort(
        (a, b) =>
          fromWallClock(b.bookingDateTime) - fromWallClock(a.bookingDateTime),
      );

    // What kinds of appointment this customer books, most frequent first.
    const byService = Object.entries(
      bookings.reduce((acc, b) => {
        const name = b.serviceName || "—";
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {}),
    )
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return {
      upcoming,
      past,
      byService,
      next: upcoming[0] ?? null,
      totalCount: bookings.length,
      placesCount: new Set(bookings.map((b) => b.organizationName)).size,
    };
  }, [bookings]);

  return { bookings, loading, error, reload: load, ...derived };
};
